import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryClient';
import type { JobStatus } from '@/types/optimization';

// Job health monitoring types
interface JobHealth {
  healthy: boolean;
  staleJobs: number;
  recentFailures: number;
  highFailureRateJobs: number;
  issues: string[];
}

interface JobHealthResponse {
  success: boolean;
  health: JobHealth;
  activeJobs: number;
  timestamp: string;
}

// Type for job history query result
interface JobHistoryItem {
  id: string;
  feed_id: string;
  status: JobStatus;
  optimization_type: 'title' | 'description' | 'both';
  total_products: number;
  completed_products: number;
  failed_products: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
}

/**
 * P2 FIX: Hook for monitoring job health
 * Provides real-time visibility into optimization job status
 */
export function useJobHealth() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['job-health', user?.id],
    queryFn: async (): Promise<JobHealthResponse> => {
      const { data, error } = await supabase.functions.invoke('check-job-failures', {
        body: { user_id: user?.id }
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    enabled: !!user?.id,
    // Check every 5 minutes
    refetchInterval: 5 * 60 * 1000,
    // Keep data fresh for 2 minutes
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook for fetching optimization job history with proper types
 */
export function useJobHistory(limit = 20) {
  const { user } = useAuth();

  return useQuery<JobHistoryItem[]>({
    queryKey: [...queryKeys.optimizationJobs.history(user?.id || ''), limit],
    queryFn: async (): Promise<JobHistoryItem[]> => {
      const { data, error } = await supabase
        .from('optimization_jobs')
        .select(`
          id,
          feed_id,
          status,
          optimization_type,
          total_products,
          completed_products,
          failed_products,
          created_at,
          started_at,
          completed_at,
          error_message
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []) as JobHistoryItem[];
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for getting active jobs count
 */
export function useActiveJobsCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: queryKeys.optimizationJobs.active(user?.id || ''),
    queryFn: async () => {
      const { count, error } = await supabase
        .from('optimization_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user?.id)
        .in('status', ['queued', 'processing']);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
    refetchInterval: (query) => {
      // More frequent polling when there are active jobs
      const activeCount = query.state.data || 0;
      return activeCount > 0 ? 3000 : 15000; // 3s when active, 15s when idle
    },
    staleTime: 2 * 1000,
  });
}

/*
 * `useRetryJob` stand hier und ist ersatzlos entfernt.
 *
 * Der Hook las `optimization_jobs.product_ids` der fehlgeschlagenen Zeile und
 * schickte sie als `product_ids` an `enqueue-optimization-batches`. Die Spalte
 * wird von beiden Erzeugern leer geschrieben (129 von 129 Jobs seit 2026-05,
 * Belege in `lib/runScore.ts`), das Schema der Function verlangt aber
 * `product_ids?.length || use_server_selection` — der Aufruf konnte also nur
 * mit 400 enden. Ein Wiederholen-Knopf, der zuverlaessig scheitert, ist
 * schlimmer als keiner.
 *
 * Entfernt statt repariert, weil der Hook exportiert war und NIRGENDS
 * aufgerufen wurde: `JobHealthIndicator` benutzt nur `useJobHealth` und
 * `useActiveJobsCount`. Eine Reparatur haette also Verhalten erfunden, das nie
 * jemand ausgeloest hat. Den Wiederholungspfad, den es wirklich gibt, faehrt
 * der Cron-Watchdog `check-job-failures` (Cap 1, `auto_retry_count`).
 *
 * Aufgefallen ist er durch die neue Verfuegbarkeits-Regel des Lauf-Sweeps
 * (`emptyJobColumnReads` in `src/test/runScope.ts`): sie meldet jeden Lesezugriff
 * auf die leere Spalte, nicht nur die in einer Messwert-Abfrage.
 */
